import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff, RefreshCw, Save } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/PageHeader';

type SettingItem = {
  key: string;
  category: string;
  label: string;
  description: string;
  type: 'text' | 'password' | 'number' | 'bool' | 'enum' | 'multiline' | 'json';
  secret: boolean;
  options?: string[];
  min?: number;
  max?: number;
  source: 'stored' | 'env' | 'default' | 'unset';
  hasValue: boolean;
  value: string;
  updatedAt: string | null;
};
type CatalogResp = { items: SettingItem[]; categories: string[] };

export function Settings() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<CatalogResp>({
    queryKey: ['/admin/api/settings'],
    queryFn: () => api.get<CatalogResp>('/admin/api/settings'),
  });

  const seed = useMutation<{ inserted: number; skipped: number; totalCatalog: number }>({
    mutationFn: () =>
      api.post<{ inserted: number; skipped: number; totalCatalog: number }>(
        '/admin/api/settings/seed',
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['/admin/api/settings'] }),
  });

  if (isLoading || !data) {
    return (
      <div>
        <PageHeader title="Platform Settings" />
        <div className="ad-card p-6 text-text3">Loading…</div>
      </div>
    );
  }

  // Group items by category
  const byCat: Record<string, SettingItem[]> = {};
  for (const it of data.items) {
    if (!byCat[it.category]) byCat[it.category] = [];
    byCat[it.category].push(it);
  }

  const totalStored = data.items.filter((i) => i.source === 'stored').length;
  const totalEnv = data.items.filter((i) => i.source === 'env').length;
  const totalUnset = data.items.filter((i) => i.source === 'unset').length;

  return (
    <div>
      <PageHeader
        title="Platform Settings"
        subtitle={`${data.items.length} settings • ${totalStored} stored, ${totalEnv} from env, ${totalUnset} unset`}
        actions={
          <button
            onClick={() => seed.mutate()}
            disabled={seed.isPending}
            className="ad-btn-secondary"
            title="Insert any catalog entries that aren't in the DB yet, using current env values"
          >
            <RefreshCw size={14} className={seed.isPending ? 'animate-spin' : ''} />
            {seed.isPending ? 'Seeding…' : 'Seed defaults'}
          </button>
        }
      />

      {seed.data && (
        <div className="ad-card p-3 mb-4 text-sm">
          ✅ Seeded {seed.data.inserted} new keys ({seed.data.skipped} already
          existed).
        </div>
      )}

      <div className="space-y-6">
        {data.categories.map((cat) => {
          const items = byCat[cat] || [];
          if (items.length === 0) return null;
          return (
            <section key={cat}>
              <h3 className="text-xs uppercase tracking-widest text-text3 mb-2 px-1">
                {cat}{' '}
                <span className="ml-2 text-text3 normal-case tracking-normal">
                  ({items.length})
                </span>
              </h3>
              <div className="ad-card divide-y divide-border">
                {items.map((it) => (
                  <SettingRow key={it.key} item={it} />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function SettingRow({ item }: { item: SettingItem }) {
  const qc = useQueryClient();
  const [v, setV] = useState(item.value);
  const [revealed, setRevealed] = useState(false);

  // When the server-side value changes (e.g. after Save), reset the
  // local edit state to match.
  if (v === '' && item.value !== '' && !revealed) {
    // (no-op — we only reset on explicit reveal/save)
  }

  const save = useMutation({
    mutationFn: (value: string) => api.put(`/admin/api/settings/${item.key}`, { value }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['/admin/api/settings'] }),
  });

  const reveal = useMutation({
    mutationFn: async () => {
      // Hits the same GET but with ?reveal=KEY — server returns
      // unmasked plaintext only if requester is super_admin.
      const r = await api.get<CatalogResp>(`/admin/api/settings?reveal=${item.key}`);
      const item2 = r.items.find((i) => i.key === item.key);
      return item2?.value ?? '';
    },
    onSuccess: (plain) => {
      setV(plain);
      setRevealed(true);
    },
  });

  const dirty = revealed ? v !== '' : v !== item.value;

  function renderEditor() {
    const common = 'ad-input w-full font-mono text-sm';
    if (item.type === 'bool') {
      return (
        <div className="flex items-center gap-2">
          <button
            onClick={() => setV(v === 'true' ? 'false' : 'true')}
            className={cn(
              'px-3 py-1.5 rounded-lg text-xs font-semibold border',
              v === 'true'
                ? 'bg-success/20 border-success/40 text-success'
                : 'bg-surface2 border-border text-text2',
            )}
          >
            {v === 'true' ? 'Enabled' : 'Disabled'}
          </button>
        </div>
      );
    }
    if (item.type === 'enum' && item.options) {
      return (
        <select
          className={common}
          value={v}
          onChange={(e) => setV(e.target.value)}
        >
          <option value="">— select —</option>
          {item.options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    }
    if (item.type === 'number') {
      return (
        <input
          type="number"
          className={common}
          min={item.min}
          max={item.max}
          value={v}
          onChange={(e) => setV(e.target.value)}
        />
      );
    }
    if (item.type === 'multiline' || item.type === 'json') {
      return (
        <textarea
          className={cn(common, 'min-h-[120px]')}
          value={v}
          onChange={(e) => setV(e.target.value)}
        />
      );
    }
    if (item.type === 'password') {
      return (
        <div className="flex gap-2">
          <input
            type={revealed ? 'text' : 'password'}
            className={cn(common, 'flex-1')}
            placeholder={item.hasValue ? '(hidden — click reveal)' : '(empty)'}
            value={v}
            onChange={(e) => setV(e.target.value)}
          />
          {!revealed && item.hasValue && (
            <button
              onClick={() => reveal.mutate()}
              disabled={reveal.isPending}
              className="ad-btn-secondary ad-btn-sm"
              title="Reveal current value (super_admin only)"
            >
              <Eye size={14} />
            </button>
          )}
          {revealed && (
            <button
              onClick={() => {
                setRevealed(false);
                setV(item.value);
              }}
              className="ad-btn-secondary ad-btn-sm"
            >
              <EyeOff size={14} />
            </button>
          )}
        </div>
      );
    }
    // text
    return (
      <input
        type="text"
        className={common}
        value={v}
        onChange={(e) => setV(e.target.value)}
        placeholder={item.value && !revealed ? item.value : ''}
      />
    );
  }

  return (
    <div className="grid gap-3 grid-cols-[1fr_2fr_auto] items-start p-4">
      <div>
        <div className="text-sm font-medium">{item.label}</div>
        <div className="text-xs text-text3 font-mono mt-0.5">{item.key}</div>
        {item.description && (
          <div className="text-xs text-text3 mt-1.5 leading-relaxed">{item.description}</div>
        )}
        <div className="mt-2 flex gap-2 flex-wrap">
          {item.secret && <span className="ad-badge ad-badge-warning">secret</span>}
          <span
            className={cn(
              'ad-badge',
              item.source === 'stored'
                ? 'ad-badge-success'
                : item.source === 'env'
                  ? 'ad-badge-accent'
                  : 'ad-badge-neutral',
            )}
          >
            {item.source}
          </span>
          <span className="ad-badge ad-badge-neutral">{item.type}</span>
        </div>
      </div>
      <div>{renderEditor()}</div>
      <button
        onClick={() => save.mutate(v)}
        disabled={!dirty || save.isPending}
        className={cn('ad-btn-primary ad-btn-sm', !dirty && 'opacity-40')}
      >
        <Save size={14} />
        {save.isPending ? 'Saving…' : 'Save'}
      </button>
      {save.error && (
        <div className="col-span-3 text-xs text-error">{(save.error as Error).message}</div>
      )}
    </div>
  );
}
